/**
 * 🚀 Modul Assistant SDK - Core SDK
 * @version 1.0.0
 * @description SDK principal para chat inteligente
 */

import { HTTPClient } from "./http-client.js";
import { ChatWidget } from "../ui/chat-widget.js";

export class ModulAssistantSDK {
  constructor() {
    this.apiKey = null;
    this.baseUrl = "http://72.167.225.86:3100";
    this.initialized = false;
    this.version = "1.0.0";

    // Estado de la conversación
    this.conversation_uuid = null;
    this.welcomeMessage = null;
    this.personalityName = null;

    // Configuración por defecto
    this.config = {
      timeout: 10000,
      retries: 3,
      debug: false,
      title: "💬 Chat",
      defaultOptions: [],
      theme: "professional",
    };

    // Componentes
    this.httpClient = new HTTPClient(this.baseUrl, this.config);
    this.chatWidget = new ChatWidget(this.config);
  }

  /**
   * 🔑 Inicializa el SDK con la API key
   * @param {Object} options - Opciones de configuración
   * @param {string} options.apiKey - API key para autenticación
   * @param {string} [options.baseUrl] - URL base de la API (opcional)
   * @param {boolean} [options.debug] - Modo debug (opcional)
   * @param {string} [options.title] - Título del chat (opcional)
   * @param {Array} [options.defaultOptions] - Opciones por defecto del chat (opcional)
   * @param {string} [options.theme] - Tema visual del chat (opcional)
   * @returns {Promise<Object>} Respuesta de inicialización
   */
  async init({
    apiKey,
    baseUrl,
    debug = false,
    title = "💬 Chat",
    defaultOptions = [],
    theme = "professional",
  }) {
    if (!apiKey || typeof apiKey !== "string") {
      const error =
        "❌ [Modul Assistant SDK] API key es requerida y debe ser una cadena de texto";
      console.warn(error);
      throw new Error(error);
    }

    this.apiKey = apiKey;

    // Actualizar configuración
    this.config = {
      ...this.config,
      debug,
      title,
      defaultOptions,
      theme,
    };

    if (baseUrl) {
      this.baseUrl = baseUrl;
      this.httpClient.updateBaseUrl(baseUrl);
    }

    this.httpClient.updateConfig(this.config);

    if (this.config.debug) {
      console.log("🚀 [Modul Assistant SDK] Inicializando SDK...", {
        version: this.version,
        baseUrl: this.baseUrl,
        apiKey: apiKey.substring(0, 8) + "...",
      });
    }

    try {
      // Crear la interfaz del chat automáticamente
      this.chatWidget.destroy(); // Limpiar si existe
      this.chatWidget = new ChatWidget(this.config);
      this.chatWidget.create(
        (message) => this._processMessage(message),
        (option, optionText) => this._processOption(option, optionText)
      );

      const response = await this.httpClient.makeRequest(
        "POST",
        "/assistant/external/init",
        {
          apiKey: this.apiKey,
        },
        this.apiKey
      );

      this.initialized = true;

      if (this.config.debug) {
        console.log(
          "✅ [Modul Assistant SDK] Inicialización exitosa:",
          response
        );
      }

      // Guardar datos de la conversación
      this.conversation_uuid = response.conversation_uuid;
      this.welcomeMessage = response.welcome_message || "";
      this.personalityName = response.name || "";

      // Actualizar el widget con los datos recibidos
      this.chatWidget.updateWelcomeMessage(this.welcomeMessage);
      this.chatWidget.updateDefaultOptions(this.config.defaultOptions);

      if (this.config.debug) {
        console.log(
          "💬 [Modul Assistant SDK] Conversación inicializada:",
          this.conversation_uuid
        );
      }

      return response;
    } catch (error) {
      const errorMsg = `❌ [Modul Assistant SDK] Error en inicialización: ${error.message}`;
      console.warn(errorMsg);

      // Mostrar el error en el chat en lugar del mensaje de bienvenida
      this.chatWidget.showInitializationError(error.message);

      throw new Error(errorMsg);
    }
  }

  /**
   * 🔍 Verifica la conectividad con la API
   * @returns {Promise<Object>} Respuesta del test de conectividad
   */
  async test() {
    if (!this.initialized) {
      const error =
        "⚠️ [Modul Assistant SDK] SDK no ha sido inicializado. Llama a init() primero.";
      console.warn(error);
      throw new Error(error);
    }

    try {
      const response = await this.httpClient.makeRequest(
        "GET",
        "/assistant/external/test",
        null,
        this.apiKey
      );

      if (this.config.debug) {
        console.log(
          "✅ [Modul Assistant SDK] Test de conectividad exitoso:",
          response
        );
      }

      return response;
    } catch (error) {
      const errorMsg = `❌ [Modul Assistant SDK] Error en test de conectividad: ${error.message}`;
      console.warn(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * 💬 Envía un mensaje al chat
   * @param {string} message - Mensaje a enviar
   * @returns {Promise<Object>} Respuesta del chat
   */
  async sendMessage(message) {
    if (!this.initialized) {
      throw new Error(
        "⚠️ [Modul Assistant SDK] SDK no ha sido inicializado. Llama a init() primero."
      );
    }

    if (!message || typeof message !== "string") {
      throw new Error(
        "❌ [Modul Assistant SDK] El mensaje es requerido y debe ser una cadena de texto"
      );
    }

    try {
      const response = await this.httpClient.makeRequest(
        "POST",
        "/assistant/external/converse",
        {
          message: message,
          conversation_uuid: this.conversation_uuid,
        },
        this.apiKey
      );

      if (this.config.debug) {
        console.log("💬 [Modul Assistant SDK] Mensaje enviado:", {
          message,
          response,
        });
      }

      return response;
    } catch (error) {
      const errorMsg = `❌ [Modul Assistant SDK] Error enviando mensaje: ${error.message}`;
      console.warn(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * ⚙️ Configura opciones del SDK
   * @param {Object} newConfig - Nueva configuración
   */
  configure(newConfig) {
    this.config = { ...this.config, ...newConfig };
    this.httpClient.updateConfig(this.config);

    if (this.config.debug) {
      console.log(
        "⚙️ [Modul Assistant SDK] Configuración actualizada:",
        this.config
      );
    }
  }

  /**
   * 🎨 Cambia el tema del chat
   * @param {string} newTheme - Nuevo tema
   */
  changeTheme(newTheme) {
    this.config.theme = newTheme;
    if (this.chatWidget) {
      this.chatWidget.changeTheme(newTheme);
    }
  }

  /**
   * 📊 Obtiene información del SDK
   * @returns {Object} Información del SDK
   */
  getInfo() {
    return {
      version: this.version,
      initialized: this.initialized,
      baseUrl: this.baseUrl,
      hasApiKey: !!this.apiKey,
      config: this.config,
      conversationUuid: this.conversation_uuid,
      widget: this.chatWidget ? this.chatWidget.getInfo() : null,
    };
  }

  /**
   * 🗑️ Destruye el SDK y limpia todos los recursos
   * @returns {Object} Información sobre la destrucción
   */
  destroy() {
    if (this.config.debug) {
      console.log("🗑️ [Modul Assistant SDK] Iniciando destrucción del SDK...");
    }

    try {
      // Destruir el widget
      if (this.chatWidget) {
        this.chatWidget.destroy();
        this.chatWidget = null;
      }

      // Resetear estado del SDK
      this.apiKey = null;
      this.initialized = false;
      this.conversation_uuid = null;
      this.welcomeMessage = null;
      this.personalityName = null;

      // Resetear configuración a valores por defecto
      this.config = {
        timeout: 10000,
        retries: 3,
        debug: false,
        title: "💬 Chat",
        defaultOptions: [],
        theme: "professional",
      };

      // Actualizar cliente HTTP
      this.httpClient.updateConfig(this.config);

      // Remover del objeto window si existe
      if (typeof window !== "undefined" && window.ModulAssistantSDK === this) {
        delete window.ModulAssistantSDK;
      }

      const destroyInfo = {
        destroyed: true,
        timestamp: new Date().toISOString(),
        version: this.version,
      };

      if (this.config.debug) {
        console.log(
          "✅ [Modul Assistant SDK] SDK destruido exitosamente:",
          destroyInfo
        );
      }

      return destroyInfo;
    } catch (error) {
      const errorMsg = `❌ [Modul Assistant SDK] Error durante la destrucción: ${error.message}`;
      console.warn(errorMsg);
      throw new Error(errorMsg);
    }
  }

  /**
   * 🔄 Procesa un mensaje de texto libre
   * @private
   */
  async _processMessage(message) {
    try {
      this.chatWidget.showTypingIndicator();

      const response = await this.sendMessage(message);

      this.chatWidget.hideTypingIndicator();
      this.chatWidget.addBotMessage(
        response.message ||
          "Gracias por tu mensaje. Un agente te contactará pronto."
      );
    } catch (error) {
      this.chatWidget.hideTypingIndicator();
      this.chatWidget.addBotMessage(
        "😔 Lo siento, hubo un error. Por favor intenta de nuevo."
      );

      if (this.config.debug) {
        console.error(
          "❌ [Modul Assistant SDK] Error procesando mensaje:",
          error
        );
      }
    }
  }

  /**
   * ⚡ Procesa una opción rápida
   * @private
   */
  async _processOption(option, optionText) {
    this.chatWidget.showTypingIndicator();

    try {
      // Buscar la opción en las opciones por defecto para obtener su mensaje
      const selectedOption = this.config.defaultOptions.find(
        (opt) => opt.value === option
      );
      let message = selectedOption ? selectedOption.text : optionText;

      // Enviar el mensaje de la opción como un mensaje normal
      const response = await this.sendMessage(message);

      this.chatWidget.hideTypingIndicator();
      this.chatWidget.addBotMessage(
        response.message || "Gracias por tu mensaje. Te ayudaré con eso."
      );
    } catch (error) {
      this.chatWidget.hideTypingIndicator();

      // Fallback a respuestas predefinidas si hay error en la API
      let response = "";
      switch (option) {
        case "demo":
          response =
            "🎯 ¡Excelente! Te ayudo a programar una demo personalizada. ¿Cuál es tu industria principal?";
          break;
        case "pricing":
          response =
            "💰 Te enviaré información detallada sobre precios. ¿Podrías decirme el tamaño de tu empresa?";
          break;
        case "industry":
          response =
            "🏭 Trabajamos con muchas industrias. ¿Cuál es tu sector específico?";
          break;
        case "support":
          response =
            "💬 Te conectaré con un especialista. Mientras tanto, ¿podrías darme tu nombre y empresa?";
          break;
        default:
          response = "Gracias por contactarnos. ¿En qué más puedo ayudarte?";
      }

      this.chatWidget.addBotMessage(response);

      if (this.config.debug) {
        console.error(
          "❌ [Modul Assistant SDK] Error procesando opción, usando fallback:",
          error
        );
      }
    }
  }
}
